import { useState, useRef } from 'react'
import {
  Input,
  Button,
  Conditional,
  kuiperClassNames,
  renderIcon,
} from '@opallabs/kuiper'
import { AnalyticsConsumer, TrackEvent } from '@opallabs/quanta'
import { Drag } from '../useDraggableList'
import './styles.scss'

type DraggableInputProps = {
  aside?: boolean
  deleteInput: (placementId: string) => void
  draggedFrom: number | null
  dragging: boolean
  dragOver: boolean
  dragZone: number
  id: string
  inputValue: string
  isDragged: boolean
  isNew: boolean | undefined
  maxLength?: number
  onAddPlacement: () => void
  onDragEnd: (e: Drag, track: TrackEvent, trackValue: string) => void
  onDragEnter: React.DragEventHandler<HTMLDivElement>
  onDragOver: React.DragEventHandler<HTMLDivElement>
  onDragStart: React.DragEventHandler<HTMLDivElement>
  onDrop: (e: Drag, track?: TrackEvent) => void
  placeholder?: string
  strict?: boolean
  updateInputValue: (value: string, id: string) => void
}

const DraggableInput: React.FC<DraggableInputProps> = ({
  aside = true,
  deleteInput,
  draggedFrom,
  dragging,
  dragOver,
  dragZone,
  id,
  inputValue,
  isDragged,
  isNew,
  maxLength = 30,
  onAddPlacement,
  onDragEnd,
  onDragEnter,
  onDragOver,
  onDragStart,
  onDrop,
  placeholder = 'Placement...',
  strict = true,
  updateInputValue,
}) => {
  const [value, setValue] = useState(inputValue)
  const [editing, setEditing] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const showTopDragZone =
    (draggedFrom && draggedFrom >= dragZone) || dragZone === 0
  const hasError = !value.trim() && !isNew
  const updateValue = () => {
    updateInputValue(value.trim(), id)
    // give user time to tab to delete button before we hide it
    timeoutRef.current = setTimeout(() => setEditing(false), 0)
  }
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      onAddPlacement()
    }
  }

  return (
    <li
      className={kuiperClassNames('draggable-input-row', { dragging, editing })}
    >
      <Conditional condition={showTopDragZone}>
        <div
          className={kuiperClassNames('drag-drop-zone drop-zone-top', {
            visible: dragOver,
          })}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          <div
            data-zone-id={dragZone}
            className={kuiperClassNames('drop-zone-inner')}
          />
        </div>
      </Conditional>
      <AnalyticsConsumer>
        {({ track }) => (
          <div
            className={kuiperClassNames('drag-input', { hide: isDragged })}
            data-drag-id={id}
            data-zone-id={dragZone}
            draggable={!editing}
            onDragEnd={(e: Drag) => onDragEnd(e, track, 'Reorder Placements')}
            onDragStart={onDragStart}
          >
            <Input
              aside={aside}
              autoFocus={isNew} // eslint-disable-line jsx-a11y/no-autofocus
              className={{ error: hasError }}
              name="Update Placement"
              maxLength={maxLength}
              onBlur={updateValue}
              onChange={(nextValue: string) => setValue(nextValue)}
              onFocus={() => setEditing(true)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              strict={strict}
              value={value}
            />
            <div className={kuiperClassNames('drag-options')}>
              <Button
                hideLabelText={true}
                label="Remove Placement"
                icon="delete"
                noArrow={true}
                onClick={() => deleteInput(id)}
                onFocus={() => {
                  if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current)
                  }
                  setEditing(true)
                }}
                onBlur={() => setEditing(false)}
                typeAttribute="button"
                type="nobutton"
              />
              <div className={kuiperClassNames('button')}>
                {renderIcon('grabber-ns', {
                  ariaLabel: 'Move this placement',
                  ariaMode: 'presentational',
                })}
              </div>
            </div>
          </div>
        )}
      </AnalyticsConsumer>
      <Conditional condition={!showTopDragZone}>
        <div
          className={kuiperClassNames('drag-drop-zone drop-zone-bottom', {
            visible: dragOver,
          })}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          <div
            data-zone-id={dragZone}
            className={kuiperClassNames('drop-zone-inner')}
          />
        </div>
      </Conditional>
    </li>
  )
}

export default DraggableInput
